import { Component, Input, NgZone, OnDestroy } from '@angular/core';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';

import { AnalyticsService } from '../../core/services/analytics.service';
import { coversForCategory, loadStockCover, stockCoverThumb } from '../../cover-image/stock-covers';
import { POPULAR_CATEGORIES } from '../categories';
import { AudioCaptureService } from './audio-capture.service';
import { CaptureSession } from './capture-types';
import { floorToSlot } from '../event-time';
import {
  ChatChip, ChatHost, ChatStep, chipsFor, emptyResult, meansNow, minutesUntil, nextStep, pickFromSpeech, question,
  resolveHosts, retryLine, stripLead, titleCase, untilMinutes, yesNo,
} from './chat-script';
import { EventDetailsParser, loadLocalParser, ParseResult } from './event-details-parser';
import { SpeakerService } from './speaker.service';

type Phase = 'asking' | 'waiting' | 'listening' | 'thinking';
type EndReason = 'create' | 'form' | 'closed';

/**
 * "Plan it with me": the create form, asked as a conversation. A panel docked
 * under the form speaks one question at a time (and shows it as a caption),
 * listens, and fills the field it asked about — the form stays on screen and
 * stays the source of truth. Answers come by voice (the recorder +
 * pwa-transcribe-voice), a quick-answer chip, or typing.
 *
 * Each answer is short and about one field, so the browser parser reads it
 * one question at a time. An AI parser can replace that later without the
 * conversation changing (see chat-script.ts).
 */
@Component({
  selector: 'app-voice-chat',
  templateUrl: './voice-chat.component.html',
  styleUrls: ['./voice-chat.component.scss'],
})
export class VoiceChatComponent implements OnDestroy {
  @Input() host!: ChatHost;
  @Input() surface = 'sheet';

  active = false;
  step: ChatStep | null = null;
  phase: Phase = 'waiting';
  caption = '';
  /** What we heard (or which quick answer was tapped), shown under the caption. */
  heard = '';
  chips: ChatChip[] = [];
  covers: { file: string; thumb: string }[] = [];
  typed = '';
  typingOpen = false;
  canSpeak = false;
  muted = false;

  private done = new Set<ChatStep>();
  private parser: Promise<EventDetailsParser> | null = null;
  private session: CaptureSession | null = null;
  private sub: Subscription | null = null;
  private sayToken = 0;
  private answers = 0;

  constructor(
    private recorder: AudioCaptureService,
    private speaker: SpeakerService,
    private analytics: AnalyticsService,
    private zone: NgZone,
  ) {}

  ngOnDestroy(): void {
    if (this.active) this.end('closed');
  }

  get busy(): boolean {
    return this.phase === 'thinking' || this.phase === 'listening';
  }

  get speaking(): boolean {
    return this.phase === 'asking' && !this.muted && this.speaker.supported;
  }

  get isRecap(): boolean {
    return this.step === 'recap' && this.phase !== 'thinking';
  }

  /** Typing is always offered for names; elsewhere behind the keyboard button. */
  get showTyping(): boolean {
    return !this.isRecap && this.step !== 'cover'
      && (this.typingOpen || this.step === 'name' || this.step === 'kind' || this.step === 'host' || !this.canSpeak);
  }

  get typingPlaceholder(): string {
    switch (this.step) {
      case 'name': return 'Or type the name';
      case 'kind': return 'Or type what it is';
      case 'host': return 'Or type the names';
      case 'when': return 'e.g. Saturday at 7';
      case 'long': return 'e.g. until 11, or 4 hours';
      default: return 'Type your answer';
    }
  }

  get micHint(): string {
    if (this.phase === 'listening') return 'Listening… tap when you’re done';
    if (this.phase === 'thinking') return 'Got it…';
    return this.chips.length || this.covers.length ? 'Tap to talk, or pick one' : 'Tap to talk';
  }

  // ── start and stop ─────────────────────────────────────────────────────

  /** Called from the tap on "Plan it with me" — the first line must be spoken inside that tap (iOS). */
  begin(): void {
    if (this.active) return;
    this.active = true;
    this.done.clear();
    this.answers = 0;
    this.canSpeak = this.recorder.isSupported();
    this.muted = this.speaker.muted;
    this.host.chatActiveChange(true);
    if (!this.parser) {
      this.parser = loadLocalParser();
      this.parser.catch(() => (this.parser = null));
    }
    this.analytics.capture('create_event_chat_started', {
      surface: this.surface, mic: this.canSpeak, speech_out: this.speaker.supported && !this.muted,
    });
    this.ask(false);
  }

  /** "I'll fill it in" / "Change something": back to the plain form, everything kept. */
  toForm(): void {
    this.end('form');
  }

  create(): void {
    this.end('create');
    this.host.submitFromChat();
  }

  private end(reason: EndReason): void {
    this.stopCapture();
    this.speaker.cancel();
    this.sayToken++;
    this.active = false;
    this.step = null;
    this.phase = 'waiting';
    this.host.chatAsking(null);
    this.host.chatActiveChange(false);
    this.analytics.capture('create_event_chat_ended', { surface: this.surface, reason, answers: this.answers });
  }

  toggleMute(): void {
    this.muted = !this.muted;
    this.speaker.muted = this.muted;
    if (this.muted && this.phase === 'asking') this.phase = 'waiting';
  }

  // ── asking ─────────────────────────────────────────────────────────────

  private ask(justAnswered: boolean): void {
    if (!this.active) return;
    const state = this.host.chatState();
    const step = nextStep(state, this.done);
    const account = this.host.chatAccountName;
    this.step = step;
    this.typed = '';
    this.typingOpen = false;
    this.chips = chipsFor(step, account);
    this.covers = step === 'cover'
      ? coversForCategory(state.category).map(file => ({ file, thumb: stockCoverThumb(file) }))
      : [];
    this.host.chatAsking(step);
    this.say(question(step, state, account, justAnswered));
  }

  private say(text: string): void {
    this.caption = text;
    this.phase = 'asking';
    const mine = ++this.sayToken;
    this.speaker.speak(text).then(() => this.zone.run(() => {
      if (mine === this.sayToken && this.phase === 'asking') this.phase = 'waiting';
    }));
  }

  // ── answering ──────────────────────────────────────────────────────────

  tapMic(): void {
    if (this.phase === 'thinking') return;
    if (this.phase === 'listening') {
      this.session?.stop();
      return;
    }
    this.stopCapture();
    this.speaker.cancel();
    this.sayToken++;
    this.heard = '';
    this.phase = 'listening';
    this.session = this.recorder.start({ lang: navigator.language || 'en-IN', maxSeconds: 15, silenceMs: 2200 });
    this.sub = this.session.events$.subscribe(e => {
      switch (e.type) {
        case 'transcribing':
          this.phase = 'thinking';
          break;
        case 'final':
          this.answerText(e.text, 'voice');
          break;
        case 'error':
          if (e.reason === 'aborted') return;
          this.analytics.capture('create_event_chat_mic_failed', { surface: this.surface, step: this.step, reason: e.reason });
          if (e.reason === 'mic_blocked' || e.reason === 'unsupported') {
            this.canSpeak = false;
            this.phase = 'waiting';
            this.caption = 'Your mic is off for this site, so tap or type your answers instead.';
          } else if (e.reason === 'no_speech') {
            this.say('I didn’t hear anything. Try again, or tap an answer.');
          } else if (e.reason === 'offline') {
            this.canSpeak = false;
            this.say('Talking needs a connection. Tap or type your answers for now.');
          } else {
            this.say('That didn’t come through. Try again, or tap an answer.');
          }
          break;
      }
    });
  }

  pickChip(chip: ChatChip): void {
    if (this.busy || !this.step) return;
    const step = this.step;
    this.speaker.cancel();
    this.sayToken++;
    this.heard = chip.label;
    this.answers++;
    this.analytics.capture('create_event_chat_answered', { surface: this.surface, step, input: 'chip', understood: true });

    if (chip.apply === 'other' || chip.apply === 'skip') {
      this.done.add(step);
      this.ask(true);
      return;
    }
    if (chip.apply === 'sheet') {
      this.openSheet(step === 'when' ? 'start' : 'duration');
      return;
    }
    this.host.applyChatAnswer({ ...emptyResult(), ...chip.apply }, chip.label, 'chip');
    this.done.add(step);
    this.ask(true);
  }

  sendTyped(): void {
    const text = this.typed.trim();
    if (!text || this.busy) return;
    this.answerText(text, 'typed');
  }

  async pickCover(cover: { file: string } | undefined, input: 'voice' | 'chip' = 'chip'): Promise<void> {
    if (!cover || this.phase === 'thinking') return;
    this.speaker.cancel();
    this.sayToken++;
    this.phase = 'thinking';
    try {
      const url = await loadStockCover(cover.file);
      this.host.setChatCover(url);
      this.answers++;
      this.analytics.capture('create_event_chat_answered', { surface: this.surface, step: 'cover', input, understood: true });
      this.done.add('cover');
      this.ask(true);
    } catch {
      this.say('That cover didn’t load. Try another one, or skip.');
    }
  }

  async uploadCover(): Promise<void> {
    if (this.busy) return;
    this.speaker.cancel();
    this.sayToken++;
    this.phase = 'waiting';
    await this.host.openChatSheet('cover');
    this.done.add('cover');
    this.ask(true);
  }

  skipCover(): void {
    if (this.busy) return;
    this.heard = 'Skip for now';
    this.done.add('cover');
    this.ask(true);
  }

  private async openSheet(sheet: 'start' | 'duration'): Promise<void> {
    const step = this.step;
    this.phase = 'waiting';
    await this.host.openChatSheet(sheet);
    // Picked or dismissed, the host has seen it: keep whatever is there and move on.
    if (step) this.done.add(step);
    this.ask(true);
  }

  /** One spoken or typed answer to the question on screen. */
  private async answerText(raw: string, input: 'voice' | 'typed'): Promise<void> {
    const text = (raw || '').trim();
    const step = this.step;
    if (!step) return;
    this.heard = text;
    this.phase = 'thinking';
    this.typed = '';

    if (step === 'recap') {
      const answer = yesNo(text);
      this.track(step, input, answer !== null);
      if (answer === 'yes') this.create();
      else if (answer === 'no') this.toForm();
      else this.say(retryLine('recap'));
      return;
    }

    if (step === 'cover') {
      const pick = pickFromSpeech(text);
      this.track(step, input, pick !== null);
      if (pick === 'skip') this.skipCover();
      else if (pick === 'upload') { this.phase = 'waiting'; await this.uploadCover(); }
      else if (typeof pick === 'number') await this.pickCover(this.covers[pick], 'voice');
      else this.say(retryLine('cover'));
      return;
    }

    const cleaned = stripLead(text);
    // A name answer is read without its lead-in ("let's call it …").
    const parsed = await this.parse(step === 'name' ? cleaned : text);
    const state = this.host.chatState();
    const out = emptyResult();
    const account = this.host.chatAccountName;

    switch (step) {
      case 'what': {
        // Take everything the first answer holds: "Anaya's 5th birthday, Saturday 3 to 7" fills four fields.
        Object.assign(out, parsed);
        if (parsed.hostedBy) {
          const hosts = resolveHosts(parsed.hostedBy.value, account);
          out.hostedBy = hosts ? { ...parsed.hostedBy, value: hosts } : null;
        }
        const bare = cleaned.replace(/^(?:a|an|the|my|our|it's a|it is a)\s+/i, '');
        const words = bare ? bare.split(/\s+/).length : 0;
        if (!parsed.category && words && words <= 2 && !parsed.start) {
          // "Housewarming", "a reunion": that's what it is, not what it's called.
          out.category = { value: titleCase(bare).slice(0, 50), confidence: 'medium', isNew: true };
          out.eventName = null;
        } else if (!parsed.category && !parsed.eventName && bare) {
          if (words <= 3) out.category = { value: titleCase(bare).slice(0, 50), confidence: 'medium', isNew: true };
          else out.eventName = { value: titleCase(bare).slice(0, 200), confidence: 'low' };
        }
        break;
      }
      case 'kind': {
        const bare = cleaned.replace(/^(?:a|an|the|it's a|it is a|it's an|it is an)\s+/i, '');
        out.category = parsed.category ?? (bare ? { value: titleCase(bare).slice(0, 50), confidence: 'medium', isNew: true } : null);
        break;
      }
      case 'name':
        out.eventName = parsed.eventName ?? (cleaned ? { value: titleCase(cleaned).slice(0, 200), confidence: 'medium' } : null);
        if (!state.category && parsed.category) out.category = parsed.category;
        break;
      case 'when':
        out.start = parsed.start;
        out.durationMinutes = parsed.durationMinutes;
        if (!out.start && meansNow(text)) {
          const now = DateTime.now();
          out.start = { value: { date: now.toFormat('yyyy-MM-dd'), time: floorToSlot(now) }, confidence: 'high' };
        }
        break;
      case 'long': {
        out.durationMinutes = parsed.durationMinutes;
        if (!out.durationMinutes && state.start) {
          // "until 7": an end time, counted from the start already on the form.
          const minutes = untilMinutes(state.start, text) ?? minutesUntil(state.start, parsed);
          if (minutes) out.durationMinutes = { value: minutes, confidence: 'medium' };
        }
        break;
      }
      case 'host': {
        const hosts = resolveHosts(text, account);
        out.hostedBy = hosts ? { value: hosts, confidence: 'high' } : null;
        break;
      }
    }

    const understood = !!(out.eventName || out.category || out.start || out.durationMinutes || out.hostedBy);
    this.track(step, input, understood);
    if (!understood) {
      this.say(retryLine(step));
      return;
    }
    this.host.applyChatAnswer(out, text, input);
    this.done.add(step);
    this.ask(true);
  }

  private async parse(text: string): Promise<ParseResult> {
    try {
      if (!this.parser) {
        this.parser = loadLocalParser();
        this.parser.catch(() => (this.parser = null));
      }
      const parser = await this.parser;
      return parser.parse(text, {
        now: DateTime.now(),
        locale: navigator.language || 'en-IN',
        categories: POPULAR_CATEGORIES.map(c => c.label),
      });
    } catch {
      // No parser (offline chunk load): the plain-text fallbacks above still work.
      return emptyResult();
    }
  }

  private track(step: ChatStep, input: 'voice' | 'typed', understood: boolean): void {
    if (understood) this.answers++;
    // The words themselves never go to analytics.
    this.analytics.capture('create_event_chat_answered', { surface: this.surface, step, input, understood });
  }

  private stopCapture(): void {
    this.session?.abort();
    this.sub?.unsubscribe();
    this.sub = null;
    this.session = null;
  }
}
